import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Stepper,
  Step,
  StepLabel,
  InputAdornment
} from '@mui/material';
import { Email as EmailIcon, LockReset as LockResetIcon, Lock as PasswordIcon, Key as KeyIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { authAPI } from '../../services/api';

const PasswordResetForm = ({ onSwitchToLogin }) => {
  const [step, setStep] = useState(0); // 0: request, 1: verify
  const [formData, setFormData] = useState({
    contact: '',
    resetCode: '',
    newPassword: '',
    newPasswordConfirm: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!formData.contact) {
      setError('Bitte E-Mail oder Telefonnummer eingeben');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.passwordResetRequest(formData.contact);
      setSuccess('Reset-Code wurde gesendet! Prüfe deine E-Mails.');
      setStep(1);
      
      // Debug: Show reset code in development
      if (response.data.debug) {
        setSuccess(`Reset-Code: ${response.data.debug.resetCode} (Development Mode)`);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Reset-Anfrage fehlgeschlagen');
    }
    setLoading(false);
  };

  const handleVerifyReset = async (e) => {
    e.preventDefault();
    
    if (!formData.resetCode || !formData.newPassword || !formData.newPasswordConfirm) {
      setError('Bitte alle Felder ausfüllen');
      return;
    }

    if (formData.newPassword.length < 10) {
      setError('Das neue Passwort muss mindestens 10 Zeichen lang sein');
      return;
    }

    if (!/\d/.test(formData.newPassword)) {
      setError('Das neue Passwort muss mindestens eine Zahl (0-9) enthalten');
      return;
    }

    if (formData.newPassword !== formData.newPasswordConfirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.resetCode.length !== 6) {
      setError('Reset-Code muss 6 Zeichen haben');
      return;
    }

    setLoading(true);
    try {
      await authAPI.passwordResetVerify(
        formData.contact,
        formData.resetCode,
        formData.newPassword
      );
      setSuccess('Passwort erfolgreich zurückgesetzt! Du kannst dich jetzt anmelden.');
      
      setTimeout(() => {
        onSwitchToLogin();
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || 'Passwort-Reset fehlgeschlagen');
    }
    setLoading(false);
  };

  const steps = ['Reset anfordern', 'Code eingeben'];

  return (
    <Card sx={{ maxWidth: 440, mx: 'auto' }}>
      <CardContent sx={{ p: { xs: 4, md: 5 } }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ letterSpacing: '-0.5px', mb: 1 }}>
            NebenanHilfe<Typography component="span" variant="h4" fontWeight={800} color="text.primary">.de</Typography>
          </Typography>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
            Passwort zurücksetzen
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Wir helfen dir beim Zurücksetzen
          </Typography>
        </Box>

        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {step === 0 ? (
          <Box component="form" onSubmit={handleRequestReset}>
            <TextField
              fullWidth
              label="E-Mail oder Telefonnummer"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              margin="normal"
              placeholder="max@example.com oder +49123456789"
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>,
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={!loading ? <EmailIcon /> : null}
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset-Code anfordern'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleVerifyReset}>
            <TextField
              fullWidth
              label="6-stelliger Reset-Code"
              name="resetCode"
              value={formData.resetCode}
              onChange={handleChange}
              margin="normal"
              placeholder="123456"
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2em' } }}
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><KeyIcon color="action" /></InputAdornment>,
              }}
            />

            <TextField
              fullWidth
              label="Neues Passwort"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              margin="normal"
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PasswordIcon color="action" /></InputAdornment>,
              }}
            />

            <TextField
              fullWidth
              label="Passwort bestätigen"
              name="newPasswordConfirm"
              type="password"
              value={formData.newPasswordConfirm}
              onChange={handleChange}
              margin="normal"
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PasswordIcon color="action" /></InputAdornment>,
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={!loading ? <LockResetIcon /> : null}
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700 
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Passwort zurücksetzen'}
            </Button>
          </Box>
        )}

        <Box textAlign="center">
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onSwitchToLogin}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: 'text.secondary' }}
          >
            <ArrowBackIcon fontSize="small" /> Zurück zur Anmeldung
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PasswordResetForm;
